import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi, type AccountAccess, type AccountMe, type LoginPayload } from '@api/auth';
import { HttpError, registerAuthTokenProvider, registerUnauthorizedHandler } from '@api/http';
import type { PermissionKey } from './types';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  getStoredAccessToken,
  getStoredRefreshToken,
} from './token';

export interface AuthContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  me: AccountMe | null;
  access: AccountAccess | null;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasPermission: (permission: PermissionKey) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function writeTokens(accessToken: string, refreshToken: string): void {
  window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  window.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function clearTokens(): void {
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [me, setMe] = useState<AccountMe | null>(null);
  const [access, setAccess] = useState<AccountAccess | null>(null);

  const loadAccount = useCallback(async (): Promise<void> => {
    const [nextMe, nextAccess] = await Promise.all([authApi.me(), authApi.access()]);
    setMe(nextMe);
    setAccess(nextAccess);
  }, []);

  const clearSession = useCallback(() => {
    clearTokens();
    setMe(null);
    setAccess(null);
  }, []);

  const login = useCallback(
    async (payload: LoginPayload): Promise<void> => {
      const tokens = await authApi.login(payload);
      writeTokens(tokens.accessToken, tokens.refreshToken);
      await loadAccount();
    },
    [loadAccount],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // ignore network/logout errors
    }
    clearSession();
  }, [clearSession]);

  const refreshProfile = useCallback(async (): Promise<void> => {
    await loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    registerAuthTokenProvider(getStoredAccessToken);
    registerUnauthorizedHandler(() => {
      clearSession();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    });

    let mounted = true;
    (async () => {
      const accessToken = getStoredAccessToken();
      if (!accessToken) {
        if (mounted) setIsLoading(false);
        return;
      }

      try {
        await loadAccount();
      } catch (err) {
        if (
          err instanceof HttpError &&
          err.status === 401 &&
          getStoredRefreshToken()
        ) {
          try {
            const refreshed = await authApi.refresh(getStoredRefreshToken()!);
            writeTokens(refreshed.accessToken, refreshed.refreshToken);
            await loadAccount();
          } catch {
            clearSession();
          }
        } else {
          clearSession();
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
      registerAuthTokenProvider(null);
      registerUnauthorizedHandler(null);
    };
  }, [clearSession, loadAccount]);

  const hasPermission = useCallback(
    (permission: PermissionKey): boolean => {
      return access?.permissions.includes(permission) ?? false;
    },
    [access],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading,
      isAuthenticated: Boolean(me),
      me,
      access,
      login,
      logout,
      refreshProfile,
      hasPermission,
    }),
    [isLoading, me, access, login, logout, refreshProfile, hasPermission],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
